import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  JourneyVariableAddForm,
  JourneyVariableAddInitialValues,
  JourneyVariableComponents,
  JourneyVariableAdditionalParams,
  JourneyVariableFormOptions,
  onJourneyVariableFormSaved,
  IJourneyVariableFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateJourneyVariable, PermissionEnum, } from 'atlas-shared';
import { createJourneyVariable, fetchJourneyVariable, getJourneyVariableCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateJourneyVariable> {}

const JourneyVariableFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateJourneyVariable>>();

  const param_props: IJourneyVariableFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => JourneyVariableAddForm(values, param_props, t), [values, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => JourneyVariableFormOptions(values || JourneyVariableAddInitialValues, param_props, t), [values, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateJourneyVariable, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateJourneyVariable) => save(() => createJourneyVariable(values).then(
    res => onSaved ? onSaved(res, navigate) : onJourneyVariableFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchJourneyVariable(+id)
        .then(({
          organization_id,
          alias,
          title,
          description,
        }) => {
          setCloned({
            organization_id,
            alias,
            title,
            description,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.JourneyVariable, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={JourneyVariableComponents}
        additionalParams={JourneyVariableAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...JourneyVariableAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('JOURNEY_VARIABLE')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const JourneyVariableFormAddView = React.memo(withAdd(JourneyVariableFormAdd, getJourneyVariableCreateSchema, JourneyVariableAddInitialValues));
